import { create } from "zustand";
import { persist } from "zustand/middleware";
import { queryClient } from "@/api/config/query-client";
import type { User } from "@/api/models/api-types";
import { clearPersonaSetupDismissed } from "@/features/persona-setup/lib/session-skip";

// Auth Store User interface (extends api-types User with __v)
interface AuthUser extends User {
  __v?: number;
}

interface AuthStore {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  accessToken: string | null;
  refreshToken: string | null;
  setAccessToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  setTokens: (tokens: {
    accessToken: string;
    refreshToken?: string | null;
  }) => void;
  // Geriye dönük uyumluluk için
  setToken: (token: string | null) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  isTokenExpired: () => boolean;
  // User data refresh functionality
  refreshUserData: (force?: boolean) => Promise<void>;
  lastUserRefresh: number | null;
}

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      lastUserRefresh: null,
      setUser: (user) => {
        set({ user });
        // User set edildiğinde credits'i de güncelle
        if (user && user.token) {
          // Credits store'a user token'ını sync et
          import("./use-credits-store").then((module) => {
            module.default.getState().setCredits(user.token);
          });
        }

        // LLM selection'ı kullanıcı objesinden hydrate et
        if (user?._id) {
          import("@/utils/user-state").then((us) => {
            const env = us.readUserState({ userId: user._id, version: 2 });
            const cached = env?.state?.llm;
            if (cached) {
              import("@/features/llm-selection/store/llm-selection-store").then(
                (llm) => {
                  llm.useLLMSelectionStore.setState({
                    selectedModelName: cached.selectedModelName ?? null,
                    selectedProvider: cached.selectedProvider ?? "all",
                  });
                },
              );
            }
          });
        }
      },
      setAccessToken: (token) => set({ accessToken: token }),
      setRefreshToken: (token) => set({ refreshToken: token }),
      setTokens: ({ accessToken, refreshToken }) =>
        set((state) => ({
          accessToken,
          refreshToken:
            typeof refreshToken === "undefined"
              ? state.refreshToken
              : refreshToken,
        })),
      // Geriye dönük uyumluluk: setToken -> accessToken
      setToken: (token) => set({ accessToken: token }),

      logout: () => {
        const currentUser = get().user;
        set({ user: null, accessToken: null, refreshToken: null });

        queryClient.clear();

        // Kullanıcı verilerini temizle
        if (currentUser?._id) {
          localStorage.removeItem("chats_" + currentUser._id);
          localStorage.removeItem("credits_" + currentUser._id);
          // Oturum skip'i — sonraki girişte quest tekrar açılsın
          clearPersonaSetupDismissed(currentUser._id);
          void import("@/features/persona-setup/store/persona-setup-store").then(
            (module) => {
              module.usePersonaSetupStore.getState().reset();
            },
          );
          // Not: harmony:v1:user:user-{id} korunur (llm vb. hatırlansın)
        }

        // 🧹 Tüm feature store'ları sıfırla (RAM temizliği)
        try {
          // Chat store
          import("@/features/chat/store/chat-store").then((module) => {
            module.useChatStore.getState().resetState();
          });
          // Conversations store
          import("@/features/conversations/store/conversations-store").then(
            (module) => {
              module.useConversationsStore.getState().reset();
            },
          );
          // LLM selection store
          import("@/features/llm-selection/store/llm-selection-store").then(
            (module) => {
              module.useLLMSelectionStore.getState().resetState();
            },
          );
          // Favori kalpleri — sonraki kullanıcı öncekinin favorilerini görmesin
          import("@/features/favorites/store/favorite-products-store").then(
            (module) => {
              module.useFavoriteProductsStore.getState().reset();
            },
          );
          import("@/features/favorites/store/favorites-list-store").then(
            (module) => {
              module.useFavoritesListStore.getState().reset();
            },
          );
        } catch (resetError) {
          // Sadece logla, logout'u engellemesin
          console.error("Store reset hatası:", resetError);
        }
      },

      isAuthenticated: () => {
        const state = get();
        return !!(state.user && state.accessToken && !state.isTokenExpired());
      },

      isTokenExpired: () => {
        const token = get().accessToken;
        if (!token) return true;

        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          return payload.exp < currentTime;
        } catch {
          return true;
        }
      },

      refreshUserData: async (force: boolean = false) => {
        const { accessToken, setUser, lastUserRefresh } = get();

        if (!accessToken || get().isTokenExpired()) {
          console.warn(
            "❌ [AUTH STORE] No valid token available for user data refresh",
          );
          return;
        }

        // Cache kontrolü - force=true ise skip et
        const now = Date.now();
        if (!force && lastUserRefresh && now - lastUserRefresh < 30000) {
          return;
        }

        try {
          // Dynamic import to avoid circular dependency
          const { apiClient } = await import("@/api/config/api-client");
          const { API_ENDPOINTS } = await import("@/api/config/api-endpoints");

          const response = await apiClient.get<{
            success: boolean;
            data: AuthUser;
          }>(API_ENDPOINTS.getMe);

          if (response.data) {
            setUser(response.data);
            // Refresh timestamp'ini güncelle
            set({ lastUserRefresh: now });
          } else {
            throw new Error("Invalid response structure");
          }
        } catch (refreshError: any) {
          console.error(
            "❌ [AUTH STORE] Failed to refresh user data:",
            refreshError,
          );

          // Token geçersizse logout yap
          if (refreshError.response?.status === 401) {
            get().logout();
          }

          throw refreshError;
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user
          ? {
              _id: state.user._id,
              name: state.user.name,
              email: state.user.email,
              image: state.user.image,
              isVerified: state.user.isVerified,
              verificationCode: state.user.verificationCode,
              verificationCodeExpiresAt: state.user.verificationCodeExpiresAt,
              lastVerificationSentAt: state.user.lastVerificationSentAt,
              role: state.user.role,
              token: state.user.token,
              region: state.user.region,
              // Persona gate soğuk açılışta /me beklemesin diye persona
              // alanları da persist edilir. `?? null`: JSON undefined'ı
              // düşürür; key'in varlığı use-post-login-gate'te hydration
              // sinyali olarak okunuyor.
              gender: state.user.gender ?? null,
              age: state.user.age ?? null,
              height: state.user.height ?? null,
              size: state.user.size ?? null,
              shoeSize: state.user.shoeSize ?? null,
              phoneNumber: state.user.phoneNumber ?? null,
              gate: state.user.gate,
              theme: state.user.theme,
              language: state.user.language,
              isDeleted: state.user.isDeleted,
              agreementConfirmations: state.user.agreementConfirmations,
              createdAt: state.user.createdAt,
              updatedAt: state.user.updatedAt,
              __v: state.user.__v,
            }
          : null,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);

export default useAuthStore;
